import React from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBasket, Trash2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { useCatalog } from "../context/CatalogContext.jsx";
import { DEFAULT_CATALOG } from "../config/catalog";

const CartSidebar = ({ cart, onRemoveItem, onCheckout }) => {
  const { catalog } = useCatalog();
  const data = catalog ?? DEFAULT_CATALOG;
  return (
    <aside className="md:col-span-2 space-y-4">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingBasket className="h-5 w-5" /> Tu selección
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <AnimatePresence initial={false}>
            {cart.length === 0 ? (
              <p className="text-sm text-neutral-500">Tu selección está vacía.</p>
            ) : (
              cart.map((item) => {
                const sizeMeta = data.sizes.find((s) => s.id === item.size);
                const flavorMeta = data.flavors.find((f) => f.id === item.flavor);
                const flavorLabel = flavorMeta?.label ?? (item.flavor === 'custom' ? 'Como yo lo quiero' : item.flavor);
                
                return (
                  <motion.div 
                    key={item.id} 
                    initial={{ opacity: 0, y: 10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, y: -10 }} 
                    className="rounded-soft border border-rose-200 p-3 bg-rose-50/30"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">
                          {flavorLabel}
                        </div>
                        <div className="text-xs text-neutral-600 mt-1 space-y-0.5">
                          <div>Acidez: {data.acidez[item.acidezIndex]}</div>
                          <div>Textura: {data.textura[item.texturaIndex]}</div>
                          <div>Grasas: {data.grasas[item.grasasIndex]}</div>
                          {item.toppings.length > 0 ? (
                            <div>
                              Toppings: {item.toppings
                                .map((id) => data.toppings.find((t) => t.id === id)?.label)
                                .filter(Boolean)
                                .join(", ")}
                            </div>
                          ) : (
                            <div>Sin toppings</div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          aria-label="Remove item" 
                          onClick={() => onRemoveItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>

          <Separator />

          <Button 
            className="w-full" 
            variant="pastel" 
            size="lg" 
            onClick={onCheckout} 
            disabled={cart.length === 0}
          >
            Encargar
          </Button>
        </CardContent>
      </Card>
    </aside>
  );
};

CartSidebar.propTypes = {
  cart: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
    flavor: PropTypes.string.isRequired,
    toppings: PropTypes.arrayOf(PropTypes.string).isRequired,
    acidezIndex: PropTypes.number.isRequired,
    texturaIndex: PropTypes.number.isRequired,
    grasasIndex: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
  })).isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
};

export default React.memo(CartSidebar);
